/* eslint-disable react/jsx-props-no-spreading */

import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  Box,
  Checkbox,
  Typography,
  FormControlLabel,
  FormGroup,
  Stack,
  Alert,
  Link,
} from '@worthy-npm/worthy-common-ui-components';
import { IMask } from 'react-imask';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppSelector } from '../../../app/hooks';
import {
  IRegistration,
  selectRegister,
  selectSubmitAPI,
  addActiveField,
  selectRegisterAPI,
} from '../../../slices/submitSlice';
import { CookieStore } from '../../../services/cookieStore';
import { PromoDetails } from '../../../data/promotion';
import { DEFAULT_PHONE } from '../../../services/worthyAPI';
import {
  GoogleVerified, StyledAlert, StyledForm, StyledTextField,
} from './step_registration.styles';

const VSuccess = 'images/icons/v_success.svg';

export const normalizePhoneNumber = (value: string, mask = true) => {
  if (!value) {
    return '';
  }
  // Handle DEFAULT_PHONE specially - return as-is
  if (value === DEFAULT_PHONE) {
    return value;
  }
  const masked = IMask.createMask({
    mask: '(000) 000-0000',
  });
  masked.resolve(value);
  return mask ? masked.value : masked.unmaskedValue;
};

const schema = yup.object().shape({
  firstName: yup
    .string()
    .required('Required field')
    .matches(
      /^[aA-zZ]+[aA-zZ_.,'‘’“”" -]*$/,
      "Allowed characters: Alphabet, space and . ” , '  _ -",
    )
    .min(2, 'Minimum 2 characters')
    .max(64, 'Maximum 64 characters'),
  lastName: yup
    .string()
    .required('Required field')
    .matches(
      /^[aA-zZ]+[aA-zZ_.,'‘’“”" -]*$/,
      "Allowed characters: Alphabet, space and . ” , '  _ -",
    )
    .min(2, 'Minimum 2 characters')
    .max(64, 'Maximum 64 characters'),
  email: yup
    .string()
    .required('Required field')
    .email('Enter a valid email (you@domain.com)')
    .max(64, 'Maximum 64 characters'),
  phoneNumber: yup.string().when('$forcePhoneNumber', {
    is: true,
    then: (localSchema) => localSchema
      .required('Required field')
      .test('phone-validation', 'Should be a valid phone number. e.g. (423) 456-7890', (value) => {
        if (!value) return false;
        // Allow DEFAULT_PHONE as valid
        if (value === DEFAULT_PHONE) return true;
        const normalized = normalizePhoneNumber(value, true);
        return !!(normalized && normalized.length === 14);
      })
      .transform((value) => normalizePhoneNumber(value, true)),
    otherwise: (localSchema) => localSchema
      .notRequired()
      .test('phone-validation', 'Should be a valid phone number. e.g. (423) 456-7890', (value) => {
        if (!value) return true; // Optional field
        // Allow DEFAULT_PHONE as valid
        if (value === DEFAULT_PHONE) return true;
        const normalized = normalizePhoneNumber(value, true);
        return !!(normalized && normalized.length === 14);
      })
      .nullable()
      .transform((value) => normalizePhoneNumber(value, true) || null),
  }),
});

interface IStepRegistrationFormProps {
  onSubmit: (data: IRegistration, smsConsent: boolean) => void;
  handleLoginView: (byMedium?: boolean) => void;
  verificationView: boolean;
  idx: number;
  stepName: string;
  showPromoTerms?: boolean;
}

type MandatoryFields = {
  key: 'firstName' | 'lastName' | 'email' | 'phoneNumber';
  label: string;
  inputMode: 'text' | 'email' | 'tel';
};

export function StepRegistrationForm(props: IStepRegistrationFormProps) {
  const dispatch = useDispatch();
  const { verificationView, showPromoTerms } = props;
  const registration = useAppSelector(selectRegister);
  const { errorObj } = useAppSelector(selectRegisterAPI);
  const { forcePhoneNumber } = useAppSelector(selectSubmitAPI);
  const isMandatoryPhoneLabelExperiment = CookieStore.getMandatoryPhoneLabel();
  const [checked, setChecked] = React.useState(false);
  const handleCopyPast = (fieldName: string) => {
    dispatch(addActiveField(fieldName));
  };
  const {
    setError,
    watch,
    reset,
    trigger,
    getFieldState,
    register,
    setValue,
    handleSubmit,
    control,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: '', lastName: '', email: '', phoneNumber: '',
    },
    mode: 'onBlur',
    resolver: yupResolver(schema),
    context: {
      forcePhoneNumber: forcePhoneNumber || isMandatoryPhoneLabelExperiment,
    },
  });

  useEffect(() => {
    reset({
      firstName: registration.firstName,
      lastName: registration.lastName,
      email: registration.email,
      phoneNumber: normalizePhoneNumber(registration.phoneNumber, true),
    });
  }, [registration, errorObj, reset, setError]);

  const onSubmitForm = (data: IRegistration) => {
    const { onSubmit } = props;
    onSubmit(data, checked);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const replaceLogInWithButton = (message: string) => {
    const parts = message.split('log in');
    if (parts.length === 1) {
      return message;
    }
    const { handleLoginView } = props;

    return (
      <>
        {parts[0]}
        <Link href="#" onClick={() => handleLoginView(true)} color="highlight.main">
          log in
        </Link>
        {parts[1]}
      </>
    );
  };

  const mandatoryFields: MandatoryFields[] = [
    { key: 'firstName', label: 'First Name', inputMode: 'text' },
    { key: 'lastName', label: 'Last Name', inputMode: 'text' },
    { key: 'email', label: 'Email', inputMode: 'email' },
  ];

  return (
    <>
      {verificationView && (
        <GoogleVerified>
          <img src={VSuccess} alt="" width="16px" />
          <span> Google account verified </span>
        </GoogleVerified>
      )}

      <StyledForm onSubmit={handleSubmit(onSubmitForm)} noValidate>
        <Stack gap={2}>

          {mandatoryFields.map((field) => (
            <Controller
              key={field.key}
              name={field.key}
              control={control}
              defaultValue=""
              rules={{
                onChange: () => {
                  const { invalid, isTouched } = getFieldState(field.key);
                  if (invalid || isTouched) {
                    trigger(field.key);
                  }
                },
              }}
              render={({ field: controllerField, fieldState }) => (
                <StyledTextField
                  variant="standard"
                  {...controllerField}
                  fullWidth
                  onPaste={() => handleCopyPast(field.key)}
                  disabled={verificationView}
                  required
                  data-automation={`input-field-${field.key}`}
                  id={`input-field-${field.key}`}
                  InputLabelProps={watch(controllerField.name) ? { shrink: true } : {}}
                  inputProps={{
                    inputMode: field.inputMode,
                    type: field.inputMode,
                    onChange: (e) => {
                      const prevValue = getValues(field.key);
                      // @ts-ignore
                      const { value } = e.target;

                      const isAutocomplete = value.length - prevValue.length > 1
                        && !fieldState.isTouched;
                      if (isAutocomplete) {
                        controllerField.onBlur();
                      }
                      controllerField.onChange(e);
                    },
                  }}
                  error={!!errors[controllerField.name]
                    || errorObj.validationField === field.key}
                  helperText={errors?.[controllerField.name]?.message}
                  placeholder={field.label}
                />
              )}
            />
          ))}

          <StyledTextField
            variant="standard"
            {...register('phoneNumber', {
              onChange: (e) => {
                e.target.value = normalizePhoneNumber(e.target.value);
                setValue('phoneNumber', normalizePhoneNumber(e.target.value, true));
                const { invalid, isTouched } = getFieldState('phoneNumber');
                if (invalid || isTouched) {
                  trigger('phoneNumber');
                }
              },
            })}
            value={normalizePhoneNumber(watch('phoneNumber'), true)}
            autoFocus={verificationView}
            onPaste={() => handleCopyPast('phoneNumber')}
            fullWidth
            name="phoneNumber"
            required={forcePhoneNumber || isMandatoryPhoneLabelExperiment}
            data-automation="input-field-phoneNumber"
            inputProps={{
              inputMode: 'tel',
              type: 'tel',
            }}
            error={!!errors.phoneNumber || errorObj.validationField === 'phone'}
            helperText={errors?.phoneNumber?.message}
            placeholder="Phone Number"
          />
        </Stack>

        {errorObj.message && (
          <StyledAlert variant="standard" severity="error">
            {replaceLogInWithButton(errorObj.message)}
          </StyledAlert>
        )}

        {showPromoTerms && (
          <Box>
            <Alert severity="success">{PromoDetails.promoLocked}</Alert>
          </Box>
        )}
        <FormGroup>
          <FormControlLabel
            control={(
              <Checkbox
                checked={checked}
                onChange={handleChange}
                sx={{ alignSelf: 'flex-start', paddingTop: '0', color: '#5A83FF' }}
              />
            )}
            label={(
              <Typography variant="body2" fontSize="14px">
                I agree to receive text messages regarding the sale of my item and promotional
                messages from Worthy at the phone number provided. Msg & data rates may apply. Msg
                frequency may vary. Reply STOP to end messages. Help at
                <Link href="tel:+18882220208"> 888-222-0208</Link>
              </Typography>
            )}
          />
        </FormGroup>

        <Box>
          {verificationView ? (
            <Button
              disableElevation
              fullWidth
              type="submit"
              data-automation="verification-button"
              variant="contained"
              size="large"
            >
              Continue
            </Button>
          ) : (
            <Button
              disableElevation
              fullWidth
              type="submit"
              data-automation="registration-button"
              variant="contained"
              size="large"
              color="highlight"
            >
              Sign Up
            </Button>
          )}
        </Box>
      </StyledForm>
    </>
  );
}

StepRegistrationForm.defaultProps = {
  showPromoTerms: false,
};
